import { MessageCircle, Phone, MessageSquare, Mail, Clock, PartyPopper } from 'lucide-react';
import ModalShell from './ui/ModalShell';
import Avatar from '../lib/avatar';
import { getInnerCardSurface, getTextColors } from '../pages/MojeSmena/sheetStyle';

/**
 * Karta kolegy — kdo to je a jak se mu ozvat.
 *
 * Dvojice „Zpráva | Volat" v systému existovala jen v `MojeSmena/TeamMemberSheet`,
 * a otevřela se pouze u lidí na **dnešní směně**. Kdo měl volno, byl
 * nedosažitelný: chat telefon nezná a modul Zaměstnanci má přidělený málokdo.
 *
 * **Materiál je z `sheetStyle`, ne z ploch stránky.** Karta sedí na skleněném
 * sheetu (`ModalShell` → `getGlassSurface`), přes které má podklad prosvítat.
 * Plné `--surface-elevated` uvnitř by na něm vypadalo jako nalepený panel;
 * vnitřní plochy proto berou `getInnerCardSurface` (bílá 4 % / černá 2,5 %)
 * a texty `getTextColors`, stejně jako všechny ostatní sheety v aplikaci.
 *
 * Kontakty jsou **vidět, ne jen zalisované do tlačítka** — číslo se dá přečíst
 * a nadiktovat, což u samotného tlačítka nejde.
 *
 * Volání není VoIP: `tel:` a `sms:` předají číslo telefonní aplikaci.
 *
 * @param {Object} props.clovek - { id, jmeno, avatar_url, telefon, email, pozice, smena }
 * @param {() => void} props.onClose
 * @param {(clovek) => void} [props.onZprava] - otevře konverzaci; bez něj se tlačítko nekreslí
 * @param {(clovek) => void} [props.onPodekovat] - „Díky" kolegovi (Komunikace); bez něj se nekreslí
 * @param {boolean} [props.online]
 * @param {boolean} [props.isDark]
 */
export default function KartaKolegy({ clovek, onClose, onZprava, onPodekovat, online = false, isDark = false }) {
    if (!clovek) return null;

    const barvy = getTextColors(isDark);
    const vnitrek = getInnerCardSurface(isDark);

    const cas = (t) => (t || '').toString().slice(0, 5);
    const telefonCisty = (clovek.telefon || '').replace(/\s+/g, '');

    // Pilulka kontaktu — stejný tvar jako kontakty na vizitce v Mém profilu,
    // jen s povrchem sheetu místo plochy stránky.
    const cip = {
        display: 'inline-flex', alignItems: 'center', gap: 7,
        padding: '7px 12px', borderRadius: 999,
        ...vnitrek,
        fontSize: 13, color: barvy.textSecondary, textDecoration: 'none',
        maxWidth: '100%', minWidth: 0,
    };
    const tlacitko = {
        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        padding: 12, borderRadius: 9, border: 'none', cursor: 'pointer',
        font: 'inherit', fontSize: 14, fontWeight: 700, textDecoration: 'none',
    };

    return (
        <ModalShell
            onClose={onClose}
            isDark={isDark}
            nadpis={clovek.jmeno}
            barva={barvy.accent}
            paticka={(
                <div style={{ display: 'flex', gap: 8, width: '100%' }}>
                    {onZprava && (
                        <button type="button" onClick={() => onZprava(clovek)}
                            style={{ ...tlacitko, background: barvy.accent, color: '#1a1a1f' }}>
                            <MessageCircle size={15} /> Zpráva
                        </button>
                    )}
                    {onPodekovat && (
                        <button type="button" onClick={() => onPodekovat(clovek)} aria-label={'Poděkovat ' + clovek.jmeno} title="Poděkovat"
                            style={{ ...tlacitko, flex: '0 0 48px', ...vnitrek, color: barvy.textSecondary }}>
                            <PartyPopper size={16} />
                        </button>
                    )}
                    {telefonCisty && (
                        <>
                            <a href={`tel:${telefonCisty}`} style={{
                                ...tlacitko,
                                background: `${barvy.success}1f`,
                                border: `1px solid ${barvy.success}3d`,
                                color: barvy.success,
                            }}>
                                <Phone size={15} /> Volat
                            </a>
                            {/* SMS pro „nezvedá to" — druhá nejčastější věc po volání. */}
                            <a href={`sms:${telefonCisty}`} aria-label="Poslat SMS" style={{
                                ...tlacitko,
                                flex: '0 0 48px',
                                ...vnitrek,
                                color: barvy.textSecondary,
                            }}>
                                <MessageSquare size={15} />
                            </a>
                        </>
                    )}
                </div>
            )}
        >
            <div style={{ padding: '16px 16px 6px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 13, marginBottom: 14 }}>
                    <Avatar url={clovek.avatar_url} jmeno={clovek.jmeno} size={54}
                        online={online} isDark={isDark} ramecekTecky={isDark ? '#1a1a22' : '#fff'} />
                    <div style={{ minWidth: 0 }}>
                        <p style={{
                            margin: 0, fontSize: 17, fontWeight: 700, lineHeight: 1.25,
                            color: barvy.textPrimary, overflowWrap: 'anywhere',
                        }}>{clovek.jmeno}</p>
                        {clovek.pozice && (
                            <span style={{
                                display: 'inline-block', marginTop: 5,
                                fontSize: 11, fontWeight: 600, padding: '2px 9px', borderRadius: 999,
                                background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                                border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}`,
                                color: 'var(--text-secondary)',
                            }}>{clovek.pozice}</span>
                        )}
                    </div>
                </div>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                    {clovek.telefon && (
                        <a href={`tel:${telefonCisty}`} style={cip}>
                            <Phone size={13} style={{ color: barvy.textMuted, flexShrink: 0 }} />
                            <b style={{ color: barvy.textPrimary, fontWeight: 600 }}>{clovek.telefon}</b>
                        </a>
                    )}
                    {clovek.email && (
                        <a href={`mailto:${clovek.email}`} style={{ ...cip, overflow: 'hidden' }}>
                            <Mail size={13} style={{ color: barvy.textMuted, flexShrink: 0 }} />
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {clovek.email}
                            </span>
                        </a>
                    )}
                    {clovek.smena && (
                        <span style={cip}>
                            <Clock size={13} style={{ color: barvy.textMuted, flexShrink: 0 }} />
                            Dnes {cas(clovek.smena.cas_od)}–{cas(clovek.smena.cas_do)}
                            {clovek.smena.nazev ? ` · ${clovek.smena.nazev}` : ''}
                        </span>
                    )}
                    {!clovek.telefon && !clovek.email && !clovek.smena && (
                        <span style={{ fontSize: 13, color: barvy.textMuted }}>
                            Žádné kontaktní údaje — doplní je správce v kartě zaměstnance.
                        </span>
                    )}
                </div>
            </div>
        </ModalShell>
    );
}
